import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2, Bookmark, ArrowRight } from 'react-feather';
import { getPostById, getRelatedPosts } from '../data/blogData';
import './ArticlePost.css';

const ArticlePost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const postData = getPostById(id);
    if (postData) {
      setPost(postData);
      setRelatedPosts(getRelatedPosts(parseInt(id), 3));
      window.scrollTo(0, 0);
    }
  }, [id]);

  useEffect(() => {
    if (!post || post.type === 'news') return;

    // Scroll spy for table of contents
    const handleScroll = () => {
      const sections = post.sections || [];
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= window.scrollY + 150) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!post) {
    return (
      <div className="article-loading">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="article-post-container">
      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt || post.summary,
          "image": post.heroImage || post.image,
          "datePublished": post.date,
          "author": {
            "@type": "Person",
            "name": post.author.name
          },
          "publisher": {
            "@type": "Organization",
            "name": "Brainy AI",
            "logo": {
              "@type": "ImageObject",
              "url": "https://yourdomain.com/logo.png"
            }
          }
        })}
      </script>

      {/* Navigation Header */}
      <nav className="article-nav">
        <div className="article-nav-left">
          <button onClick={() => window.history.back()} className="article-back-btn">
            <ArrowLeft size={20} />
            Back
          </button>
        </div>
        <div className="article-nav-center">
          <div className="article-logo" onClick={() => window.location.href = '/'}>
            <span>Brainy</span>
          </div>
        </div>
        <div className="article-nav-right"></div>
      </nav>

      {/* Header */}
      <header className="article-header">
        <div className="article-header-content">

          {post.category && (
            <div className="article-category-badge">{post.category}</div>
          )}

          <h1 className="article-title">{post.title}</h1>
          
          {post.subtitle && (
            <p className="article-subtitle">{post.subtitle}</p>
          )}

          <div className="article-meta">
            <div className="article-author-section">
              <img 
                src={post.author.avatar} 
                alt={post.author.name} 
                className="article-author-avatar"
              />
              <div className="article-author-info">
                <span className="article-author-name">{post.author.name}</span>
                {post.author.bio && (
                  <span className="article-author-bio">{post.author.bio}</span>
                )}
              </div>
            </div>
            <div className="article-meta-items">
              <span className="article-date">
                <Calendar size={16} />
                {post.formattedDate}
              </span>
              <span className="article-read-time">
                <Clock size={16} />
                {post.readTime} min read
              </span>
            </div>
          </div>

          <div className="article-actions">
            <button className="article-action-btn">
              <Share2 size={18} />
              Share
            </button>
            <button className="article-action-btn">
              <Bookmark size={18} />
              Save
            </button>
          </div>
        </div>

        {post.heroImage && (
          <div className="article-hero-image-container">
            <img 
              src={post.heroImage} 
              alt={post.title} 
              className="article-hero-image"
            />
          </div>
        )}
      </header>

      <div className="article-body">
        {/* Table of Contents - Only for articles with sections */}
        {post.type === 'article' && post.sections && post.sections.length > 0 && (
          <aside className="article-toc">
            <div className="article-toc-sticky">
              <h3 className="article-toc-title">Table of Contents</h3>
              <nav className="article-toc-nav">
                {post.sections.map((section) => (
                  <button
                    key={section.id}
                    className={`article-toc-item ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(section.id)}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
        )}

        {/* Article Content */}
        <article className="article-content">
          <div 
            className="article-text"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="article-tags">
              <h4>Tags:</h4>
              <div className="tags-list">
                {post.tags.map((tag) => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Author Card */}
          <div className="article-author-card">
            <img 
              src={post.author.avatar} 
              alt={post.author.name} 
              className="author-card-avatar"
            />
            <div className="author-card-info">
              <h4 className="author-card-name">{post.author.name}</h4>
              {post.author.bio && (
                <p className="author-card-bio">{post.author.bio}</p>
              )}
            </div>
          </div>
        </article>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="related-posts-section">
          <div className="related-posts-content">
            <h2 className="related-posts-title">Related Articles</h2>
            <div className="related-posts-grid">
              {relatedPosts.map((relatedPost) => (
                <article
                  key={relatedPost.id}
                  className="related-post-card"
                  onClick={() => window.location.href = `/blog/${relatedPost.id}`}
                >
                  {relatedPost.type === 'article' && relatedPost.image && (
                    <div className="related-post-image-container">
                      <img 
                        src={relatedPost.image} 
                        alt={relatedPost.title} 
                        className="related-post-image"
                      />
                    </div>
                  )}
                  <div className="related-post-content">
                    {relatedPost.category && (
                      <span className="related-post-category">{relatedPost.category}</span>
                    )}
                    <h3 className="related-post-title">{relatedPost.title}</h3>
                    <p className="related-post-excerpt">
                      {relatedPost.excerpt || relatedPost.summary}
                    </p>
                    <div className="related-post-meta">
                      <Clock size={14} />
                      <span>{relatedPost.readTime} min read</span>
                    </div>
                  </div>
                  <div className="related-post-arrow">
                    <ArrowRight size={20} />
                  </div>
                </article>
              ))}
            </div>
            <div className="related-posts-cta">
              <button 
                onClick={() => window.location.href = '/blog'} 
                className="view-all-posts-btn"
              >
                View All Posts
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="article-footer">
        <p>&copy; {new Date().getFullYear()} Brainy AI. All rights reserved.</p>
        <div className="article-footer-links">
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
        </div>
      </footer>
    </div>
  );
};

export default ArticlePost;

